import { dequal } from 'dequal'
import { AuthToken, CustomerContext } from '@typesApp/Customer'
import { config } from '@config/config'
import { isCustomerNumberChanged, isCustomerNumberValid, normalizeCustomerNumber } from './isCustomerNumberValid'

const DEFAULT_GUEST_CONTEXT: CustomerContext = {
  customerType: 'GUEST',
}

type BuildGuestCustomerContextParams = {
  userId: string | undefined
  cartId: string | undefined
}

export const buildGuestCustomerContext = (params: BuildGuestCustomerContextParams): CustomerContext => {
  const { userId, cartId } = params ?? {}

  return {
    ...DEFAULT_GUEST_CONTEXT,
    userId: normalizeCustomerNumber(userId),
    cartId: normalizeCartId(cartId),
  }
}

export const normalizeCartId = (id: string | number | null | undefined): string => `${id ?? ''}`.trim()

type ShouldInitCustomerContextParams = {
  inProgress?: boolean
  isTokenError?: boolean
  customerContext: CustomerContext | undefined
  currentCustomerContext: CustomerContext | undefined
}

/**
 * @returns true if customer context should be initialized
 */
export const shouldInitCustomerContext = (params: ShouldInitCustomerContextParams): boolean => {
  const { inProgress, isTokenError, customerContext, currentCustomerContext } = params

  if (!customerContext || inProgress || isTokenError) return false
  if (
    customerContext.customerType === 'ANONYMOUS' &&
    currentCustomerContext?.customerType &&
    currentCustomerContext.customerType !== 'ANONYMOUS'
  )
    return false
  return isCustomerContextChanged(customerContext, currentCustomerContext)
}

/**
 * Check for equality ignoring userId.
 * Currently LCR is not sending userId in the customer context even for registered customer.
 * Once LCR integration is updated, we can include the userId in the comparision.
 */
const isCustomerContextChanged = (
  source: CustomerContext | undefined,
  target: CustomerContext | undefined
): boolean => {
  const { userId: sourceUserId, ...normalizedSource } = source ?? {}
  const { userId: targetUserId, ...normalizedTarget } = target ?? {}
  return !dequal(normalizedSource, normalizedTarget)
}

type ShouldInitAuthTokenParams = {
  inProgress?: boolean
  isTokenError?: boolean
  isLoggingOut?: boolean
  isSessionExpired?: boolean
  isTokenReady?: boolean
  authToken: AuthToken | undefined
  customerContext: CustomerContext | undefined
}

/**
 * @returns true if auth token can be initialized
 */
export const shouldInitAuthToken = (params: ShouldInitAuthTokenParams): boolean => {
  const { inProgress, isTokenError, isLoggingOut, isSessionExpired, isTokenReady, authToken, customerContext } = params

  if (inProgress || isTokenError || isLoggingOut || isSessionExpired) return false

  return (!isTokenReady || isRequireNewToken(customerContext, authToken)) && canRequestAuthToken(customerContext)
}

const isRequireNewToken = (customerContext: CustomerContext | undefined, authToken: AuthToken | undefined): boolean => {
  if (!authToken) return true
  const { userId } = customerContext ?? {}
  const { customerNumber } = authToken ?? {}

  const contextCustomerNumber = normalizeCustomerNumber(userId)
  const tokenCustomerNumber = normalizeCustomerNumber(customerNumber)

  const isCustomerChanged = isCustomerNumberChanged(contextCustomerNumber, tokenCustomerNumber)
  const isCustomerTypeChanged = Boolean(
    authToken?.customerType && customerContext?.customerType !== authToken?.customerType
  )

  return isCustomerChanged || isCustomerTypeChanged
}

const canRequestAuthToken = (customerContext: CustomerContext | undefined): boolean => {
  if (!customerContext) return false

  // guest auth token not supported for some brands
  const validForGuest =
    !config.getFeatures()?.disableAuthTokenForGuest &&
    customerContext.customerType === 'GUEST' &&
    isCustomerNumberValid(customerContext.userId)

  const validForAuthenticated = customerContext.customerType === 'REGISTERED'

  return validForGuest || validForAuthenticated
}

export const isNewCustomerAfterSessionExpired = (
  customerContext: CustomerContext,
  currentCustomerContext: CustomerContext | undefined,
  isSessionExpired: boolean | undefined
): boolean => {
  if (!isSessionExpired) return false
  return (
    Boolean(customerContext?.userId) &&
    Boolean(currentCustomerContext?.userId) &&
    customerContext.userId !== currentCustomerContext?.userId
  )
}

export const isGuestTransitionToAuthenticated = (
  customerContext: CustomerContext,
  currentCustomerContext: CustomerContext | undefined
): boolean => {
  if (!currentCustomerContext) return false

  return customerContext.customerType === 'REGISTERED' && currentCustomerContext.customerType === 'GUEST'
}

export const isAuthenticatedTransitionToGuest = (
  customerContext: CustomerContext,
  currentCustomerContext: CustomerContext | undefined
): boolean => {
  if (!currentCustomerContext) return false

  return customerContext.customerType === 'GUEST' && currentCustomerContext.customerType === 'REGISTERED'
}
